import { RepoContext } from '../hooks/task/useRepoContexts'

/**
 * Repository source types for display purposes.
 */
export type RepoSourceType = 'uploaded' | 'local' | 'git' | 'unknown'

/**
 * Splits a repository identifier into [orgName, repoName].
 *
 * Mirrors the backend's `get_org_name_and_repo_name` (chat/builder.py): the
 * last `/`-separated segment is the repo, everything before it is the org.
 * This handles GitLab nested groups (`group/subgroup/repo`), local_sync
 * paths (`device_id/2024-abc___name`), and plain `org/repo` uniformly.
 *
 * Returns `['', repository]` for a single-segment identifier, mirroring the
 * server-side fallback in `repo-metadata` route.
 */
export function parseRepositoryName(repository: string | undefined | null): [string, string] {
  if (!repository) return ['', '']
  const segments = repository.split('/')
  if (segments.length < 2) return ['', repository]
  const repoName = segments[segments.length - 1]
  const orgName = segments.slice(0, -1).join('/')
  return [orgName, repoName]
}

/**
 * Extracts display name from an S3/local file path.
 *
 * Handles multiple formats:
 * 1. New format with ___: date-handleId___originalFileName.tar.gz -> originalFileName
 * 2. Path format: prefix/tier/date/org/user/filename.tar.gz -> filename (without .tar.gz)
 * 3. Simple filename: repo.tar.gz -> repo
 *
 * @param filePath - S3 path or filename string
 */
export function extractFileDisplayName(filePath: string): string {
  // Handle new format with triple underscore (e.g., "2024-abc123___myproject.tar.gz")
  if (filePath.includes('___')) {
    const dirname = filePath.split('___').pop()?.replace('.tar.gz', '')
    return dirname || filePath
  }

  // Handle path format - extract just the filename and remove .tar.gz extension
  if (filePath.includes('/')) {
    const filename = filePath.split('/').pop() || filePath
    return filename.replace(/\.tar(\.gz)?$/, '')
  }

  // Fallback: remove extension if it's a simple filename
  return filePath.replace(/\.tar(\.gz)?$/, '')
}

/**
 * Determines the source type of a repository context.
 */
export function getRepoSourceType(ctx: RepoContext): RepoSourceType {
  if (ctx.connType === 'S3' && ctx.s3TarFilePath) {
    return 'uploaded'
  }

  if (ctx.connType === 'local_sync') {
    return 'local'
  }

  if (ctx.orgName && ctx.repoName) {
    return 'git'
  }

  return 'unknown'
}

/**
 * Gets the suffix to display after a repository name based on its source.
 *
 * @param ctx - Repository context
 * @param branchName - Optional branch name for git repos
 */
export function getRepoSuffix(ctx: RepoContext, branchName?: string): string {
  const sourceType = getRepoSourceType(ctx)

  switch (sourceType) {
    case 'uploaded':
      return 'uploaded'
    case 'local':
      return 'local'
    case 'git':
      return branchName || ''
    default:
      return ''
  }
}

/**
 * Extracts just the repository display name (without suffix).
 */
export function getRepoDisplayName(ctx: RepoContext): string {
  const sourceType = getRepoSourceType(ctx)

  switch (sourceType) {
    case 'uploaded':
      return extractFileDisplayName(ctx.s3TarFilePath || '')
    case 'local':
      // For local sync, extract from s3TarFilePath or use repoName
      if (ctx.s3TarFilePath) {
        return extractFileDisplayName(ctx.s3TarFilePath)
      }
      return ctx.repoName || 'Local Repository'
    case 'git':
      return `${ctx.orgName}/${ctx.repoName}`
    default:
      return 'No Repository'
  }
}

/**
 * Renders a repository context into a full display string with suffix.
 *
 * Format examples:
 * - S3 uploads: "project-name (uploaded)"
 * - Local sync: "my-project (local)"
 * - Git repos: "org/repo (branch)" or "org/repo"
 *
 * @param ctx - Repository context
 * @param options - Formatting options
 */
export function renderRepoContext(
  ctx: RepoContext,
  options?: {
    showBranch?: boolean
    branchName?: string
  }
): string {
  const displayName = getRepoDisplayName(ctx)
  const sourceType = getRepoSourceType(ctx)

  switch (sourceType) {
    case 'uploaded':
      return `${displayName} (uploaded)`
    case 'local':
      return `${displayName} (local)`
    case 'git': {
      const branch = options?.branchName ?? ctx.branchName
      if (options?.showBranch !== false && branch) {
        return `${displayName} (${branch})`
      }
      return displayName
    }
    default:
      return displayName
  }
}
